/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  forwardRef,
  ViewChild,
  Output,
  EventEmitter,
  ElementRef,
  SimpleChanges,
  ChangeDetectorRef,
  Injector,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { ObjectUtils } from "@bfx/primeng/utils";
/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  UserPromiseLookupService,
  StaticDataPrivatizationType,
  ClientAccountVisibilityType,
  BranchPromiseLookupService,
  ClientAccountVisibilityService,
  AccountLookupService,
  ClientLookupService,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
// models
import {
  ICAutocompleteClientModel,
  ICAutocompleteTabModel,
  ICAutocompleteAccountModel,
  ICAutocompleteGenericModel,
} from "./models/bfx-interface-controls.models";
import {
  ICNInputControlState,
  ICNAutocompleteMode,
  ICNAutocompleteFavoriteType,
  ICNAutocompleteFavoriteItemFormat,
  OrderEntryAccountDisplay,
  OrderEntryClientDisplay,
} from "./models/bfx-interface-controls.enums";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";
import { BfxAutocompleteFavoritesProviderService } from "./services/bfx-autocomplete-favorites-provider.service";

export const AUTOCOMPLETE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICAutocompleteComponent),
  multi: true,
};

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEAccountMediatorService } from "../../services/basic/oe-account-mediator.service";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { OELookupService } from "../../services/basic/oe-lookup.service";
import { OEApiService } from "../../services/oe-api.service";
import { ChangeDetectionStrategy } from "@angular/core";
import { SizeMixin } from "shared/base-components/mixins/size";
import { EmphasisMixin } from "shared/base-components/mixins/emphasis";
import { InputLabelStateMixin } from "shared/base-components/mixins/label-state";
import { DisabledMixin } from "shared/base-components/mixins/disabled";
import { ComponentMixin } from "shared/base-components/mixins/mixin";
import { BfxInputDirective } from "shared/base-components/bfx-input";

const Mixins = SizeMixin(EmphasisMixin(InputLabelStateMixin(DisabledMixin(ComponentMixin))));

@Component({
  selector: "ic-autocomplete",
  templateUrl: "./html/ic-autocomplete.html",
  providers: [AUTOCOMPLETE_VALUE_ACCESSOR, ObjectUtils],
  host: {
    "[class.interface-control__custom-input-autocomplete__container]": "true",
    "[class.fieldsGroup]": "fieldsGroup",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICAutocompleteComponent
  extends Mixins
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  /***************************************/
  /* INPUT / OUTPUT */

  /* if true threats null or empty value as valid (its used in order entry defaults) */
  @Input("allowEmpty") input_allowEmpty: boolean = false;

  @Input("disabled") input_disabled: boolean;

  @Input("mode") input_mode: string;

  @Input("dataset") input_dataset: Array<{ value: number; label: string }>;

  @Input("tabs") input_tabs: Array<ICAutocompleteTabModel>;

  @Input("constraint") input_constraint: any;

  @Input("constraintAutoSelect") input_constraintAutoSelect: boolean;

  @Input("label") input_label: any = "Type";

  @Input("entryType") entryType: OENEntryType = OENEntryType.SPOT;

  @Input() autoHighlightFirstIfNoValue: boolean = null;

  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  inputFocused: boolean = false;

  @ViewChild("inputField", { static: true })
  inputField: BfxAutoCompleteComponent; //ElementRef;

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  /* External Validation */
  input_externalState: ICNInputControlState;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  /* Client input to Account Input on select id transfer */
  @Output() onClientSelect: EventEmitter<string> = new EventEmitter<string>(); // on client autocomplete

  //
  @Output() onDatasetChange: EventEmitter<Array<any>> = new EventEmitter<Array<any>>();

  @Input("clientSelect") input_predictedClient: string; // on account autocomplete

  /**
   * replace autocomplete header with a label matching field group in design system
   */
  @Input("fieldsGroup") fieldsGroup: boolean = false;

  /***************************************/
  /* DATA */
  public config: {
    resultsToShow: number;
    favoritesSize: number;
    removeFavoriteDuplicates: boolean;
  } = {
    resultsToShow: 50,
    favoritesSize: 3,
    removeFavoriteDuplicates: true,
  };

  public locked: boolean = false;
  public modeString: string;

  public data = {
    disabled: false, // indicates whether the component is disabled
    label: "", // autocomplete label
    // selection
    item: null, // used in the autocomplete interface for display (intermediate model)
    value: null, // item selected in the autocomplete in native (Impl model)
    _dataset: [], // complete dataset of all autocomplete options, used for local filtering,
    _datasetEmitter: null,
    set datasetEmitter(value) {
      this._datasetEmitter = value;
      this._datasetEmitter.emit(this._dataset);
    },
    get datasetEmitter() {
      return this._datasetEmitter;
    },
    set dataset(value) {
      this._dataset = value;
      if (this._datasetEmitter != null) {
        this.datasetEmitter.emit(this._dataset);
      }
    },
    get dataset() {
      return this._dataset;
    },
    dataset_input_constraint: null,
    filteredItems: [], //
    // profile/state
    mode: ICNAutocompleteMode.TYPE,
    state: ICNInputControlState.INACTIVE, // validity state of the selected/inputted item
    // privatization
    uiPrivatized: false,
    autocompleteField: "label",
    selectedAutocompleteDisplayField: null,
  };

  private accountMetadata: {
    datasetForPredictedClientId: string;
    datasetForClientId: string;
    /*  lookupCounterLimit: 3,
        lookupCounter: 0,
        validateCounterLimit: 5,
        validateCounter: 0,
        loadingDataset: false, */
  } = {
    datasetForPredictedClientId: null,
    datasetForClientId: null,
    /*   lookupCounterLimit: 3,
        lookupCounter: 0,
        validateCounterLimit: 5,
        validateCounter: 0,
        loadingDataset: false, */
  };

  private clientMetadata: {
    query: string;
    queryTerminal: boolean;
    datasetForAccountId: string;
  } = {
    query: null,
    queryTerminal: false,
    datasetForAccountId: null,
  };

  /** should we perform validation on inactive autocomplete? After toggling allowEmpty, changes should be always monitored */
  private validateInactive: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    injector: Injector,
    public element: ElementRef,
    public objectUtils: ObjectUtils,
    public translationService: TranslateService,
    protected _branchLookupService: BranchPromiseLookupService,
    protected _clientLookupService: ClientPromiseLookupService,
    protected _accountLookupService: AccountPromiseLookupService,
    protected _userLookup: UserPromiseLookupService,
    protected _favoritesService: BfxAutocompleteFavoritesProviderService,
    protected _accountMediatorService: OEAccountMediatorService,
    protected oeLookup: OELookupService,
    protected oeApi: OEApiService,
    protected visibilityService: ClientAccountVisibilityService,
    public cd: ChangeDetectorRef,
    protected systemConfigService: SystemConfigPromiseService,
    public inputDirective: BfxInputDirective,
  ) {
    super(injector);
    this.size = "sm";
    this.registerUserModelUpdates();
  }

  ngOnInit() {
    this.initializeOnInit();
    if (null == this.autoHighlightFirstIfNoValue) {
      // if nobody set it - default to true, unless its a Client
      this.autoHighlightFirstIfNoValue = this.data.mode !== ICNAutocompleteMode.CLIENT;
    }
    this.inputField.autoHighlightFirstIfNoValue = this.autoHighlightFirstIfNoValue;
    this.data.datasetEmitter = this.onDatasetChange;
  }

  ngOnChanges(changes: SimpleChanges) {
    let hasUnhandled = false;
    for (let propName in changes) {
      switch (propName) {
        case "entryType":
          break;
        case "input_predictedClient":
          if (this.data.mode !== ICNAutocompleteMode.ACCOUNT) {
            hasUnhandled = true;
          }
          break;
        case "input_allowEmpty":
          const change = changes[propName];
          this.validateInactive = !change.firstChange;
          this.autocompleteValidate();
          break;
        default:
          hasUnhandled = true;
          break;
      }
    }
    if (hasUnhandled) {
      this.initializeOnChanges();
    }
  }

  ngOnDestroy() {}

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    try {
      this.data.value = value;
      this.data.item = this.transformModel(value);
      this.autocompleteValidate();
    } catch (ex) {
      logger.out.error("EXCEPTION: BfxAutocompleteInputComponent.writeValue(): ", ex);
    }
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */
  public onFocus($event) {
    this.inputFocused = true;
    this.data.state = ICNInputControlState.ACTIVE;
  }
  public onBlur($event) {
    if (this.data.mode === ICNAutocompleteMode.CLIENT) {
      this.clientBlur(); // Fast search->blur adjustment for CLIENT
    } else {
      this.inputFocused = false;
      this.inputField.blurInput();
      this.autocompleteValidate();
      this.onModelTouched();
      this.blur.emit(this.data.value);
    }
    this.validateInactive = true;
  }

  /**
   * The blur function below is specifically written for the client mode.
   * It performs client value adjustment/correction for the search (typing) followed by a fast blur (tab out)
   * problem described in https://www.bcdfx.com/ijira/browse/OMS-28963
   *
   * The problem arises because suggestion (promise based) filtering is performed in the wrapper (this component), while the
   * blur is triggered by the low level autocomplete. So, if the blur event triggers before the suggestion filtering/search is done,
   * the low level autocomplete component sends (auto-selected) value from a previous suggestions iteration (ie. not taking into account all the
   * entered search characters). Therefore, the blur value may be incorrect in terms of what the user has typed.
   *
   * The correction procedure uses meta-query which contains the last string change of the search query. If criteria are met
   * (ie. incorrect blur value is detected), the blur is interrupted and value is reselected as the first element in the
   * suggestions array (after filtering has completed).
   */
  private clientBlur(): void {
    if (
      typeof this.clientMetadata.query === "string" &&
      this.clientMetadata.query.length > 0 &&
      ((this.clientMetadata.queryTerminal &&
        this.data.item instanceof ICAutocompleteClientModel &&
        this.data.item["id"].toLowerCase().indexOf(this.clientMetadata.query.toLowerCase()) < 0) ||
        (!this.clientMetadata.queryTerminal && typeof this.data.item === "string"))
    ) {
      // Client value adjustment
      this.clientLookup({ query: this.clientMetadata.query }); //.then((matched)=>{
      if (this.data.filteredItems instanceof Array && this.data.filteredItems.length > 0) {
        if (true == this.autoHighlightFirstIfNoValue) {
          // set first value if any
          this.data.item = this.data.filteredItems[0];
        }
        //logger.out.info("CLIENT BLUR- Setting data.item to ", `${this.data.item instanceof ICAutocompleteClientModel ? this.data.item.clientId :  this.data.item}`);
      } else {
        // set data item to query string => should result in validation error (since no appropriate client)
        //logger.out.info("CLIENT BLUR- Setting data.item to " + this.clientMetadata.query );
        this.data.item = "" + this.clientMetadata.query;
        //this.data.item = null;
        let temp = "" + this.clientMetadata.query;
        setTimeout(() => {
          this.inputField.inputEL.nativeElement.value = temp;
        }, 0);
      }
      this.inputFocused = false;
      this.clientValidate();
      this.onModelTouched();
      this.blur.emit(this.data.value);
      // reset meta-query
      this.clientMetadata.query = null;
      //logger.out.info("CLIENT AUTO-COMPLETE => Adjustment in effect");
      //});
    } else {
      // NO need for client Adjustment
      this.inputFocused = false;
      this.clientValidate();
      this.onModelTouched();
      this.blur.emit(this.data.value);
      // reset meta-query
      this.clientMetadata.query = null;
      //logger.out.info("CLIENT BLUR => NO Adjustment | value=", this.data.value);
    }
    this.cd.markForCheck();
  }

  // only used for client
  public onLowLevelModelChanged($event, data): void {
    if (this.data.mode === ICNAutocompleteMode.CLIENT) {
      if (typeof $event === "string" && $event.length > 0) {
        this.clientMetadata.queryTerminal = false;
        this.clientMetadata.query = $event;
      } else if ($event instanceof ICAutocompleteClientModel) {
        this.clientMetadata.queryTerminal = true;
      }
      //logger.out.info("AUTO-COMPLETE CHANGE: $event=", $event);
      this.cd.markForCheck();
    }
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  public hidePanel() {
    this.inputFocused = false;
    this.inputField.hide();
  }

  public onSelect($event) {
    if (this.data.mode === ICNAutocompleteMode.CLIENT) {
      if ($event == null || $event.value == null || $event.value === "") {
        this.onClientSelect.emit(null);
      } else if ($event instanceof ICAutocompleteClientModel) {
        // client value adjustment query is ready to be applied
        this.clientMetadata.queryTerminal = true;
        // account prediction
        this._accountMediatorService.processClient($event.value);
        this.onClientSelect.emit($event.value);
        this.onBlur($event);
      }
      //logger.out.info("AUTO-COMPLETE SELECT: $event=", $event);
    } else {
      this.onBlur($event);
    }
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public reset(): void {
    this.data.uiPrivatized = false;
    this.data.disabled = false;
    this.data.label = "";
    this.data.item = "";
    this.data.value = null;
    this.validateInactive = false;

    this.data.dataset = [];
    this.data.filteredItems = [];

    this.externalState = null;

    this.initializeOnInit();
    this.data.state = ICNInputControlState.INACTIVE;
    this.onChange(this.data.value);
  }

  public lock(): void {
    this.setLocked(true);
  }

  public unlock(): void {
    if (!this.data.uiPrivatized) {
      this.setLocked(false);
    }
  }

  private setLocked(value: boolean) {
    this.locked = value;
    this.disabled = value;
    this.inputLabelState = value ? "disabled" : "enabled";
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  protected initializeOnInit(): void {
    this.initializeSelectedMode();

    if (this.input_disabled != null && typeof this.input_disabled === "boolean") {
      this.data.disabled = this.input_disabled;
    } else {
      this.data.disabled = false;
    }

    switch (this.data.mode) {
      case ICNAutocompleteMode.TYPE:
        this.modeString = "orderType";
        this.data.label =
          this.input_label != null && this.input_label !== "" ? this.input_label : "Type";
        this.data.dataset = this.input_dataset;
        //                this.data.disabled = true;
        break;
      case ICNAutocompleteMode.ACCOUNT:
        this.modeString = "account";
        this.data.label = "Account";
        this.data.autocompleteField = "smartLabel";
        this.data.selectedAutocompleteDisplayField = "name";
        this.systemConfigService
          .getSystemConfig("system.features.OrderEntryAccountDisplay")
          .then((value) => {
            this.data.selectedAutocompleteDisplayField = OrderEntryAccountDisplay.parse(value);
          });
        break;
      case ICNAutocompleteMode.CLIENT:
        this.modeString = "client";
        this.data.label = "Client";
        this.data.autocompleteField = "smartLabel";
        this.data.selectedAutocompleteDisplayField = "clientName";
        this.systemConfigService
          .getSystemConfig("system.features.OrderEntryClientDisplay")
          .then((value) => {
            this.data.selectedAutocompleteDisplayField = OrderEntryClientDisplay.parse(value);
          });
        // disable if no available clients OMS-31121
        let availableClients = this.oeLookup.clientsForOrderEntryNow(this.entryType, "", 0);
        if (
          availableClients == null ||
          (Array.isArray(availableClients) && availableClients.length === 0)
        ) {
          this.lock();
        }

        break;
      case ICNAutocompleteMode.CLIENT_TRADER:
        this.modeString = "clientTrader";

        this.data.label =
          this.input_label != null && this.input_label !== "" ? this.input_label : "client trader";
        this.data.disabled = true;
        break;
      default:
        logger.out.error(
          'ERROR: BfxAutocompleteInputComponent.initializeOnInit(): Invalid mode "',
          this.input_mode,
          '"',
        );
        break;
    }
  }

  public initializeOnChanges(): void {
    if (this.input_externalState != null) {
      this.data.state = this.input_externalState;
    }

    if (this.input_disabled === false || this.input_disabled == null) {
      switch (this.data.mode) {
        case ICNAutocompleteMode.TYPE:
          this.data.dataset = this.input_dataset;

          break;
        case ICNAutocompleteMode.ACCOUNT:
          this.accountLoadDataset();
          break;
        case ICNAutocompleteMode.CLIENT:
          this.clientLoadDataset();
          break;
        case ICNAutocompleteMode.CLIENT_TRADER:
          this.clientTraderLoadDataset();
          break;

        default:
          logger.out.error(
            'ERROR: BfxAutocompleteInputComponent.initializeOnChanges(): Invalid mode: "',
            this.input_mode,
            '"',
          );
          break;
      }
    }
  }

  protected initializeSelectedMode() {
    if (this.input_mode != null) {
      let mode = this.input_mode.toUpperCase();
      switch (mode) {
        case "TYPE":
          this.data.mode = ICNAutocompleteMode.TYPE;
          break;
        case "ACCOUNT":
          this.data.mode = ICNAutocompleteMode.ACCOUNT;
          break;
        case "CLIENT":
          this.data.mode = ICNAutocompleteMode.CLIENT;
          break;
        case "CLIENT_TRADER":
          this.data.mode = ICNAutocompleteMode.CLIENT_TRADER;
          break;
        default:
          logger.out.error(
            'ERROR: BfxAutocompleteInputComponent.initializeSelectedMode(): Invalid mode: "',
            this.input_mode,
            '"',
          );
          break;
      }
    } else {
      this.data.mode = ICNAutocompleteMode.TYPE;
    }
  }

  /**********************************************************************/
  /* SHARED METHODS */

  public autocompleteSearch($event): void {
    switch (this.data.mode) {
      case ICNAutocompleteMode.TYPE:
        this.lookupOrderType($event);
        break;
      case ICNAutocompleteMode.ACCOUNT:
        this.accountLookup($event);
        break;
      case ICNAutocompleteMode.CLIENT:
        this.clientLookup($event);
        break;
      case ICNAutocompleteMode.CLIENT_TRADER:
        this.clientTraderLookup($event);
        break;
    }
  }

  public autocompleteValidate() {
    if (!this.data.uiPrivatized) {
      switch (this.data.mode) {
        case ICNAutocompleteMode.TYPE:
          this.validateOrderType();
          break;
        case ICNAutocompleteMode.ACCOUNT:
          this.accountValidate();
          break;
        case ICNAutocompleteMode.CLIENT:
          this.clientValidate();
          break;
        case ICNAutocompleteMode.CLIENT_TRADER:
          this.clientTraderValidate();
          break;
      }
      this.cd.markForCheck();
    }
  }
  /**
   * Transforms Impl model into a intermediate
   */
  protected transformModel(item: any): any {
    try {
      if (item != null) {
        switch (this.data.mode) {
          case ICNAutocompleteMode.ACCOUNT:
            return this.accountTransformResult(item);
          case ICNAutocompleteMode.CLIENT:
            return this.clientTransformResult(item);

          case ICNAutocompleteMode.CLIENT_TRADER:
            if (item instanceof CubiqTS.UserImpl) {
              return this.clientTraderTransformResult(item);
            } else if (item instanceof ICAutocompleteGenericModel) {
              return item;
            }
            break;
          case ICNAutocompleteMode.TYPE: // no transformation just check
            if (item instanceof ICAutocompleteGenericModel) {
              return item;
            } else {
              logger.out.error(
                `ERROR: Order Type autocomplete transformation is in invalid format! dump %O`,
                item,
              );
              return item;
            }
        }
      } else {
        return item;
      }
    } catch (ex) {
      logger.out.error(`ERROR: isAutocomplete.transformModel(): Exception: ${ex}`);
      return null;
    }
  }

  protected updateFavorites(item: any): void {
    if (item != null) {
      switch (this.data.mode) {
        case ICNAutocompleteMode.ACCOUNT:
          let accountId: string =
            item instanceof CubiqTS.AccountImpl ? this.transformModel(item).value : item.value;
          return this._favoritesService.addFavoriteItem(
            ICNAutocompleteFavoriteType.ACCOUNT,
            accountId,
          );

        case ICNAutocompleteMode.CLIENT:
          let clientId: string =
            item instanceof CubiqTS.ClientImpl ? this.transformModel(item).value : item.value;
          return this._favoritesService.addFavoriteItem(
            ICNAutocompleteFavoriteType.CLIENT,
            clientId,
          );
      }
      this.cd.markForCheck();
    }
  }

  protected updateDatasetLabels(): void {
    for (let item of this.data.dataset) {
      item.label = item["id"];
    }
    this.cd.markForCheck();
  }
  /**
   * newValue should be Impl or null
   */
  public setDataValue(
    newValue: any,
    valid: boolean = false,
    registerChange: boolean = true,
    updateItem: boolean = true,
    blur: boolean = true,
    isLoadPermitted: boolean = true,
  ) {
    if (!this.data.uiPrivatized) {
      if (!ObjectUtils.equals(this.data.value, newValue)) {
        // this.data.value == null ||
        this.data.value = newValue;
        if (updateItem) {
          this.data.item = this.transformModel(newValue);
        }
        if (registerChange) {
          // MEDIATOR
          if (this.data.value instanceof CubiqTS.ClientImpl) {
            this._accountMediatorService.processClient(this.data.value);
          }

          this.onChange(this.data.value);
        }
      }

      if (valid && isLoadPermitted) {
        this.updateFavorites(newValue);

        // OMS-26975: clientTrader dataset changed externally in dialog since load
        // if we've been provided valid newValue not in dataset. reload dataset
        // or if we've a valid newValue of null check if we've deleted all client traders
        // TODO: consider something like initializeOnChanges if happens for other types
        if (ICNAutocompleteMode.CLIENT_TRADER === this.data.mode)
          if (
            (newValue === null && Array.isArray(this.data.dataset)) ||
            !this.data.dataset ||
            (Array.isArray(this.data.dataset) &&
              this.data.dataset.some((element) => {
                return (
                  this.data &&
                  this.data.item &&
                  typeof this.data.item.value === "string" &&
                  element &&
                  typeof element.value === "string" &&
                  this.data.item.value === element.value
                );
              }) === false)
          ) {
            this.clientTraderLoadDataset();
          }

        this.data.state = ICNInputControlState.VALID;
        this.externalState = null;
      }
    }
    this.cd.markForCheck();
  }

  public setUIPrivatized(privatizationLabel: string): void {
    this.data.uiPrivatized = true;
    this.inputField.writeValue({ label: privatizationLabel });
    this.lock();
  }

  private isAutoSelectEnabled(): boolean {
    return typeof this.input_constraintAutoSelect === "boolean" && this.input_constraintAutoSelect;
  }

  /**********************************************************************/
  /* EXCLUSIVE METHODS */

  /********************/
  /* ORDER TYPE */

  private lookupOrderType(event): void {
    let filteredItems = [];
    let allItems = this.data.dataset;
    let queryTokens = typeof event?.query === "string" ? event.query.split(" ") : [""];

    for (let i = 0; i < allItems.length; i++) {
      let item = allItems[i];
      let labelTokens = item.label.split(" ");
      if (
        event.query.length === 0 ||
        ((queryTokens.length === 1 || queryTokens.length === labelTokens.length) &&
          queryTokens.every((queryToken, i) =>
            labelTokens[i].toLowerCase().startsWith(queryToken.toLowerCase()),
          ))
      ) {
        filteredItems.push(item);
      }
    }
    this.data.filteredItems = filteredItems;
  }

  public validateOrderType(): boolean {
    let selectedItem = this.data.item;
    if (selectedItem != null && selectedItem !== "") {
      let result = this.data.dataset.find((item) => {
        if (selectedItem.label != null) {
          return item.label.toLowerCase() === selectedItem.label.toLowerCase();
        }
        return item.label.toLowerCase() === selectedItem.toLowerCase();
      });
      if (result != null) {
        this.data.state = ICNInputControlState.VALID;
        this.externalState = null;
        /* this.input_externalState = null;
                // this.externalStateChange.emit(this.input_externalState); */
        this.data.value = result;
        this.data.item = result;
        this.onChange(result);
        return true;
      }
    } else {
      if (this.data.state !== ICNInputControlState.INACTIVE && !this.input_allowEmpty) {
        this.data.state = ICNInputControlState.INVALID;
        this.data.value = null;
        this.onChange(null);
        return false;
      } else if (this.data.state !== ICNInputControlState.INACTIVE && this.input_allowEmpty) {
        this.data.state = ICNInputControlState.INACTIVE;
        this.data.value = null;
        this.onChange(null);
        return true;
      }
    }
    if (this.data.state !== ICNInputControlState.INACTIVE) {
      this.data.state = ICNInputControlState.INVALID;
    }
    this.data.value = null;
    this.onChange(null);
    this.cd.markForCheck();
    return false;
  }

  /********************/
  /* ACCOUNT */

  /**
   * This method checks if the selected account is for a client that has only one account and disables account input editing
   * Developed specifically to address: https://www.bcdfx.com/ijira/browse/OMS-23792
   */
  public checkAndLockAccountForClient(): void {
    if (
      this.input_constraint instanceof CubiqTS.ClientImpl &&
      (StaticDataPrivatizationType.hasPrivatization(this.input_constraint) ||
        !ClientAccountVisibilityType.isFull(this.input_constraint))
    ) {
      // constraints are a no-op when privatized or not fully visible
      return;
    }

    if (this.input_constraint instanceof CubiqTS.ClientImpl) {
      this._accountMediatorService.getAccountsForClient(this.input_constraint).then((resultSet) => {
        if (resultSet instanceof Array && resultSet.length === 1) {
          setTimeout(() => {
            if (this.data.value != null && resultSet[0].id === this.data.value.id) {
              this.data.disabled = true;
              this.cd.markForCheck();
            }
          }, 0);
        }
      });
    }
  }

  /**
   * This method should be invoked when the account constraint (the selected client) changes,
   * It creates/loads a dataset of AccountImpls, associated with that client, which are transformed into BfxAutocompleteAccountModel.
   * In case the dataset consists of a single account it is immediately selected.
   */
  protected accountLoadDataset(): void {
    if (
      this.input_constraint instanceof CubiqTS.ClientImpl &&
      (StaticDataPrivatizationType.hasPrivatization(this.input_constraint) ||
        ClientAccountVisibilityType.cantSee(this.input_constraint))
    ) {
      return;
    }

    if (this.input_constraint instanceof CubiqTS.ClientImpl) {
      if (this.accountMetadata.datasetForClientId !== this.input_constraint.clientId) {
        // load only dataset is not for the current client
        let accounts: Array<CubiqTS.AccountImpl> = this.oeLookup.accountsForClientIncludingBreakNow(
          this.input_constraint,
          true,
        );
        this.data.dataset = accounts;
        this.accountMetadata.datasetForClientId = this.input_constraint.clientId;

        if (accounts == null || (Array.isArray(accounts) && accounts.length === 0)) {
          // client has no associated accounts
          this.data.disabled = true;
          this.data.dataset = null;
          this.setDataValue(null, false, true);
          this.onModelTouched();
          this.inputField.blurInput();
        } else {
          let selectionOK = false;
          if (this.data.value != null && this.data.value instanceof CubiqTS.AccountImpl) {
            selectionOK =
              accounts.filter((item) => {
                return item.id === this.data.value.id;
              }).length >= 1;
          }

          if (!selectionOK) {
            if (this.isAutoSelectEnabled() && Array.isArray(accounts) && accounts.length === 1) {
              //
              this.setDataValue(accounts[0], true, true);
              this.onModelTouched();
              if (this.data.value != null && accounts[0].id === this.data.value.id) {
                this.inputField.blurInput();
                this.data.disabled = true;
                this.hidePanel();
              }
            } else if (
              this.isAutoSelectEnabled() &&
              Array.isArray(accounts) &&
              accounts.length > 1 &&
              typeof (this.input_constraint as CubiqTS.ClientImpl).defaultAccountCd === "string"
            ) {
              if (this.data.disabled) {
                this.data.disabled = false;
              }

              let accountToSet = accounts.find(
                (item) => item.id === this.input_constraint.defaultAccountCd,
              );
              if (accountToSet instanceof CubiqTS.AccountImpl) {
                this.setDataValue(accountToSet, true, true);
                this.onModelTouched();
                if (this.data.value != null && accountToSet.id === this.data.value.id) {
                  this.inputField.blurInput();
                  this.hidePanel();
                }
              } else {
                setTimeout(() => {
                  this.inputField.focusInput();
                }, 0);
              }
            } else {
              // if nothing selected, remove previous
              if (this.data.disabled) {
                this.data.disabled = false;
                setTimeout(() => {
                  this.inputField.focusInput();
                }, 0);
              }
              //setTimeout( () => { }, 0);
              this.setDataValue(null, false, true);
              setTimeout(() => {
                this.accountValidate();
              }, 0);
            }
          } else if (
            selectionOK &&
            Array.isArray(accounts) &&
            accounts.length === 1 &&
            !this.data.disabled
          ) {
            // Added to fix: https://www.bcdfx.com/ijira/browse/OMS-23975
            setTimeout(() => {
              if (this.data.value != null && accounts[0].id === this.data.value.id) {
                this.data.disabled = true;
              } else {
                this.data.disabled = false;
              }
              this.cd.markForCheck();
            }, 0);
          } else if (
            selectionOK &&
            Array.isArray(accounts) &&
            accounts.length > 1 &&
            this.data.disabled
          ) {
            // Added to fix: https://www.bcdfx.com/ijira/browse/OMS-24885
            this.data.disabled = false;
          }
        }
      }
    } else {
      if (this.data.disabled) {
        this.data.disabled = false;
        //setTimeout( () => { this.inputField.focusInput(); }, 0); // Removed because of OMS-34101
      }
      this.accountMetadata.datasetForClientId = null;
      this.data.dataset = null;
    }
    this.cd.markForCheck();
  }

  private accountLookup(event: any): void {
    try {
      let searchToken: string =
        typeof event.query === "string" ? event.query.toLowerCase() : event.query;
      let resultFavorites: Array<ICAutocompleteAccountModel> =
        new Array<ICAutocompleteAccountModel>();
      let resultRegular: Array<ICAutocompleteAccountModel> =
        new Array<ICAutocompleteAccountModel>();

      const favoriteAccounts: Array<ICAutocompleteAccountModel> =
        this._favoritesService.getFavoriteItems(
          ICNAutocompleteFavoriteType.ACCOUNT,
          ICNAutocompleteFavoriteItemFormat.DISPLAY,
        ) as Array<ICAutocompleteAccountModel>;

      // CLIENT CONSTRAINT IS SET
      if (
        this.input_constraint instanceof CubiqTS.ClientImpl &&
        Array.isArray(this.data.dataset) &&
        this.data.dataset.length > 0
      ) {
        if (this.input_constraint.clientId === this.accountMetadata.datasetForClientId) {
          this.accountLoadDataset();
        }
        // SEARCH FAVORITES
        if (Array.isArray(favoriteAccounts) && favoriteAccounts.length > 0) {
          for (let transformedFavoriteAccount of favoriteAccounts) {
            let favoriteAccount = this.data.dataset.find(
              (item) => item.id === transformedFavoriteAccount?.id,
            );
            if (
              favoriteAccount != null &&
              favoriteAccount instanceof CubiqTS.AccountImpl &&
              (AccountLookupService.isMatch(searchToken, favoriteAccount) ||
                searchToken.length == 0)
            ) {
              resultFavorites.push(this.accountTransformResult(favoriteAccount));

              if (
                this.config.resultsToShow > 0 &&
                resultFavorites.length >= this.config.resultsToShow
              ) {
                break;
              }
            }
          }
        }
        // SEARCH DATASET
        if (Array.isArray(this.data.dataset) && this.data.dataset.length > 0) {
          for (let account of this.data.dataset) {
            // dataset should be CubiqTS.AccountImpl[]
            let transformedAccount = this.accountTransformResult(account);

            if (AccountLookupService.isMatch(searchToken, account) || searchToken.length == 0) {
              if (
                resultFavorites == null ||
                (Array.isArray(resultFavorites) && resultFavorites.length === 0) ||
                (Array.isArray(resultFavorites) &&
                  resultFavorites.length > 0 &&
                  resultFavorites.findIndex((item) => item?.id === transformedAccount.id) < 0)
              ) {
                resultRegular.push(transformedAccount);
              }
            }

            if (
              this.config.resultsToShow > 0 &&
              resultRegular.length >= this.config.resultsToShow - resultFavorites.length
            ) {
              break;
            }
          }
        }
      } else {
        // CLIENT CONSTRAINT IS NOT SET

        // SEARCH FAVORITES
        if (Array.isArray(favoriteAccounts) && favoriteAccounts.length > 0) {
          if (searchToken == null || searchToken === "") {
            resultFavorites = favoriteAccounts;
          } else {
            resultFavorites = favoriteAccounts.filter((item) =>
              AccountLookupService.isMatch(searchToken, item),
            );
          }
          // remove inappropriate accounts
          //resultFavorites = resultFavorites.filter(item=> this.clientVisibleAndAvailableForEntryType(item));

          // update label based on selected tab
          if (Array.isArray(resultFavorites) && resultFavorites.length > 0) {
            resultFavorites = resultFavorites.map((item) =>
              item instanceof CubiqTS.AccountImpl
                ? this.accountTransformResult(item)
                : item instanceof ICAutocompleteAccountModel
                  ? this.accountTransformResult(this.oeLookup.accountNow(item.id))
                  : item,
            );
          }
        }

        let accounts: Array<CubiqTS.AccountImpl> = this.oeLookup.accountsForOrderEntryNow(
          this.entryType,
          searchToken,
          this.config.resultsToShow,
        );
        // remove inappropriate accounts
        //accounts = accounts.filter(item => this.clientVisibleAndAvailableForEntryType(item));
        resultRegular = this.accountTransformResults(accounts);
      }
      /* *********** */
      // HANDLE FAVORITES
      if (Array.isArray(resultFavorites) && resultFavorites.length > 0) {
        // remove if too many favorites
        while (resultFavorites.length > this.config.favoritesSize) {
          resultFavorites.pop();
        }
        // sort favorites
        if (Array.isArray(resultFavorites) && resultFavorites.length > 1) {
          resultFavorites.sort((a, b) => a["id"].localeCompare(b["id"]));
        }
        // add line on the last favorite
        resultFavorites[resultFavorites.length - 1].lastFavorite = true;
      }

      // HANDLE REGULARS
      // avoid duplicates in regular items and trim to results to show config
      let tempResultRegular: Array<ICAutocompleteAccountModel> =
        Array<ICAutocompleteAccountModel>();
      for (
        let i = 0;
        i < resultRegular.length && i < this.config.resultsToShow - resultFavorites.length;
        i++
      ) {
        if (
          !this.config.removeFavoriteDuplicates ||
          resultFavorites.findIndex((item) => item?.id === resultRegular[i].id) < 0
        ) {
          tempResultRegular.push(resultRegular[i]);
        }
      }
      resultRegular = tempResultRegular;

      // sort regulars
      if (Array.isArray(resultRegular) && resultRegular.length > 1) {
        resultRegular.sort((a, b) => a["id"].localeCompare(b["id"]));
      }

      // ASSIGNMENT
      if (
        Array.isArray(resultFavorites) &&
        resultFavorites.length > 0 &&
        Array.isArray(resultRegular) &&
        resultRegular.length > 0
      ) {
        this.data.filteredItems = resultFavorites.concat(resultRegular);
      } else if (Array.isArray(resultFavorites) && resultFavorites.length > 0) {
        this.data.filteredItems = resultFavorites;
      } else if (Array.isArray(resultRegular) && resultRegular.length > 0) {
        this.data.filteredItems = resultRegular;
      } else {
        this.data.filteredItems = [];
      }
      this.cd.markForCheck();
    } catch (ex) {
      logger.out.error("ERROR: BfxAutocompleteInputComponent.lookupAccount(): Exception:", ex);
    }
  }

  protected accountValidate(): boolean {
    this.cd.markForCheck();
    try {
      if (this.inputFocused === true) {
        this.data.state = ICNInputControlState.ACTIVE;
        return true;
      }
      let selectedItem = this.data.item;

      if (selectedItem != null && selectedItem !== "") {
        // Account input IS NOT EMPTY
        // CLIENT constraint SET
        if (this.input_constraint instanceof CubiqTS.ClientImpl) {
          if (this.accountMetadata.datasetForClientId !== this.input_constraint.clientId) {
            this.accountLoadDataset();
          }

          let matchedAccount = null;
          if (Array.isArray(this.data.dataset) && this.data.dataset.length > 0) {
            let index = null;
            for (let i = this.data.dataset.length - 1; i >= 0; i--) {
              if (selectedItem instanceof Object && selectedItem.id != null) {
                if (this.data.dataset[i].id === selectedItem.id) {
                  index = i;
                  break;
                }
              } else if (typeof selectedItem === "string") {
                // EXACT MATCH
                if (
                  selectedItem.toLowerCase() ===
                  this.data.dataset[i][this.data.selectedAutocompleteDisplayField].toLowerCase()
                ) {
                  index = i;
                  break;
                }
              } else {
                break;
              }
            }
            matchedAccount = this.data.dataset[index];
          }

          if (matchedAccount instanceof CubiqTS.AccountImpl) {
            this.setDataValue(matchedAccount, true, true);
            return true;
          } else {
            return this.accountSetToNull();
          }

          // Client constraint NOT SET
        } else {
          if (selectedItem instanceof Object && typeof selectedItem.id === "string") {
            // if account input is object
            if (selectedItem.hasPrivatization || !selectedItem.hasFullVisibility) {
              return true;
            } else {
              let account = this.oeLookup.accountWithVisibilityNow(
                selectedItem.id,
                ClientAccountVisibilityType.Full,
              );
              if (account instanceof CubiqTS.AccountImpl) {
                this.setDataValue(account, true, true);
                return true;
              } else {
                return this.accountSetToNull();
              }
            }
          } else if (typeof selectedItem === "string") {
            // if account input is string
            let accountSet: Array<CubiqTS.AccountImpl> = this.oeLookup.accountsForOrderEntryNow(
              this.entryType,
              selectedItem,
              0,
            );
            let index = null;
            if (Array.isArray(accountSet) && accountSet.length >= 1) {
              for (let i = 0; i < accountSet.length; i++) {
                // EXACT MATCH
                if (
                  selectedItem.toLowerCase() ===
                  accountSet[i][this.data.selectedAutocompleteDisplayField].toLowerCase()
                ) {
                  index = i;
                  break;
                }
              }
            }

            if (typeof index === "number") {
              this.setDataValue(accountSet[index], true, true);
              return true;
            } else {
              return this.accountSetToNull();
            }
          } else {
            return this.accountSetToNull();
          }
        }
      } else {
        // Account input IS EMPTY
        if (
          (this.validateInactive || this.data.state !== ICNInputControlState.INACTIVE) &&
          !this.input_allowEmpty
        ) {
          this.data.state = ICNInputControlState.INVALID;
          this.setDataValue(null, false, true, false);
          return false;
        } else if (this.data.state !== ICNInputControlState.INACTIVE && this.input_allowEmpty) {
          this.data.state = ICNInputControlState.INACTIVE;
          this.setDataValue(null, false, false, false, true);
          return true;
        } else {
          return this.accountSetToNull();
        }
      }
    } catch (ex) {
      logger.out.error("ERROR: BfxAutocompleteInputComponent.validateAccount(): Exception:", ex);
      return this.accountSetToNull();
    }
  }

  private accountSetToNull(): boolean {
    if (this.data.state !== ICNInputControlState.INACTIVE) {
      this.data.state = ICNInputControlState.INVALID;
    }
    this.setDataValue(null, false, true, false);
    return false;
  }

  private accountTransformResults(
    data: Array<CubiqTS.AccountImpl>,
  ): Array<ICAutocompleteAccountModel> {
    let result = Array<ICAutocompleteAccountModel>();
    for (let accountImpl of data) {
      try {
        let item = this.accountTransformResult(accountImpl);
        result.push(item);
      } catch (ex) {
        logger.out.error("EXCEPTION: BfxAutocompleteInputComponent.clientTransformResults(): ", ex);
      }
    }
    return result;
  }

  private accountTransformResult(account: CubiqTS.AccountImpl): ICAutocompleteAccountModel {
    if (account != null && account instanceof CubiqTS.AccountImpl) {
      let model = new ICAutocompleteAccountModel(); // value => id
      model.id = account.id;
      model.name = account.name;
      model.bookingCode = account.bookingCode;
      model.contact = account.contact;
      //
      model.value = account.id;

      model.label = `${account[this.data.selectedAutocompleteDisplayField]}`;
      model.smartLabel = `${model.id} ｜ ${model.name} ｜ ${model.bookingCode}`;
      model.hasPrivatization = StaticDataPrivatizationType.hasPrivatization(account);
      model.hasFullVisibility = ClientAccountVisibilityType.isFull(account);
      return model;
    } else {
      logger.out.warn("Can not transform, Item is null or wrong type (AccountImpl)!");
    }
  }

  /********************/
  /* CLIENT */

  /**
   * This method should be invoked when the client constraint (the selected account) changes,
   * It creates/loads a dataset of ClientImpls, associated with that account, which are transformed into ICAutocompleteClientModel[].
   * - IF the dataset consists of a single client it is immediately selected.
   * - IF
   */
  private clientLoadDataset(): void {
    if (
      this.input_constraint instanceof CubiqTS.AccountImpl &&
      (StaticDataPrivatizationType.hasPrivatization(this.input_constraint) ||
        ClientAccountVisibilityType.cantSee(this.input_constraint))
    ) {
      return;
    }

    if (this.input_constraint instanceof CubiqTS.AccountImpl) {
      if (this.clientMetadata.datasetForAccountId !== this.input_constraint.id) {
        let account = this.input_constraint;
        this.clientMetadata.datasetForAccountId = account.id;
        let clients: Array<CubiqTS.ClientImpl> = null;
        if (account.salesGroupId instanceof CubiqTS.Long) {
          clients = this.oeLookup.clientsForBreakAccountNow(this.input_constraint);
        } else {
          clients = this.oeLookup.clientsForAccountNow(this.input_constraint);
        }

        clients =
          Array.isArray(clients) && clients.length > 0
            ? clients.filter((item) => this.clientVisibleAndAvailableForEntryType(item))
            : clients;

        if (clients == null || (Array.isArray(clients) && clients.length == 0)) {
          //this.data.disabled = true;
          this.data.dataset = null;

          this.setDataValue(null, false, true);
          this.onModelTouched();
          this.inputField.blurInput();
        } else {
          this.data.dataset = clients;

          // check if client should be the same as the one previously selected (ie. the selected client is in local dataset)
          let selectionOK = false;
          if (this.data.value instanceof CubiqTS.ClientImpl) {
            selectionOK =
              clients.find((item) => item.clientId === this.data.value.clientId) != null;
          }

          if (!selectionOK) {
            // new selection is needed
            if (
              this.isAutoSelectEnabled() &&
              Array.isArray(clients) &&
              clients.length === 1 &&
              clients[0] instanceof CubiqTS.ClientImpl
            ) {
              // only one client
              this.setDataValue(clients[0], true, true);
              this.onModelTouched();
              this.inputField.blurInput();
              this.hidePanel();
            } else if (this.isAutoSelectEnabled() && Array.isArray(clients) && clients.length > 1) {
              // select default client
              let clientToSelect = clients.find((item) => item.clientId === account.clientId);
              if (clientToSelect instanceof CubiqTS.ClientImpl) {
                this.setDataValue(clientToSelect, true, true);
                this.onModelTouched();
                this.inputField.blurInput();
                this.hidePanel();
              } else {
                setTimeout(() => {
                  this.inputField.focusInput();
                }, 0);
                this.setDataValue(null, false, true);
                setTimeout(() => {
                  this.clientValidate();
                }, 0);
              }
            }
          }
        }
      }
    } else {
      this.clientMetadata.datasetForAccountId = null;
      this.data.dataset = null;
    }
  }

  private clientLookup(event): void {
    try {
      let searchToken: string =
        typeof event.query === "string" ? event.query.toLowerCase() : event.query;

      let resultFavorites: Array<ICAutocompleteClientModel> =
        new Array<ICAutocompleteClientModel>();
      let resultRegular: Array<ICAutocompleteClientModel> = new Array<ICAutocompleteClientModel>();

      const favoriteClients: Array<ICAutocompleteClientModel> =
        this._favoritesService.getFavoriteItems(
          ICNAutocompleteFavoriteType.CLIENT,
          ICNAutocompleteFavoriteItemFormat.DISPLAY,
        ) as Array<ICAutocompleteClientModel>;

      /* STRICT */
      if (
        this.oeApi.isClientDatasetModeStrict() &&
        this.input_constraint instanceof CubiqTS.AccountImpl &&
        Array.isArray(this.data.dataset) &&
        this.data.dataset.length > 0
      ) {
        // ACCOUNT (client dataset) IS SET

        if (this.clientMetadata.datasetForAccountId !== this.input_constraint.id) {
          this.clientLoadDataset();
        }
        // SEARCH FAVORITES
        if (Array.isArray(favoriteClients) && favoriteClients.length > 0) {
          for (let transformedFavoriteClient of favoriteClients) {
            // favoriteClient is ICAutocompleteClientModel
            let favoriteClient = this.oeLookup.clientNow(transformedFavoriteClient.id);
            if (
              favoriteClient != null &&
              favoriteClient instanceof CubiqTS.ClientImpl &&
              this.clientVisibleAndAvailableForEntryType(favoriteClient) &&
              ClientLookupService.isMatch(searchToken, favoriteClient)
            ) {
              resultFavorites.push(this.clientTransformResult(favoriteClient));

              if (
                this.config.resultsToShow > 0 &&
                resultFavorites.length >= this.config.resultsToShow
              ) {
                break;
              }
            }
          }
        }

        // SEARCH DATASET
        for (let client of this.data.dataset) {
          // dataset should be CubiqTS.ClientImpl[]
          if (this.clientVisibleAndAvailableForEntryType(client)) {
            let transformedClient = this.clientTransformResult(client);

            if (
              this.clientVisibleAndAvailableForEntryType(client) &&
              ClientLookupService.isMatch(searchToken, client)
            ) {
              if (
                resultFavorites == null ||
                (Array.isArray(resultFavorites) && resultFavorites.length === 0) ||
                (Array.isArray(resultFavorites) &&
                  resultFavorites.length > 0 &&
                  resultFavorites.findIndex((item) => item.id === transformedClient.id) < 0)
              ) {
                resultRegular.push(transformedClient);
              }
            }

            if (
              this.config.resultsToShow > 0 &&
              resultRegular.length >= this.config.resultsToShow - resultFavorites.length
            ) {
              break;
            }
          }
        }
        /* LEGACY */
      } else {
        // ACCOUNT (client dataset) IS NOT SET (or invalid)
        // SEARCH FAVORITES
        if (Array.isArray(favoriteClients) && favoriteClients.length > 0) {
          if (searchToken == null || searchToken === "") {
            resultFavorites = favoriteClients.filter((item) => {
              let favoriteClient = this.oeLookup.clientNow(item.id);

              return (
                favoriteClient != null &&
                favoriteClient instanceof CubiqTS.ClientImpl &&
                this.clientVisibleAndAvailableForEntryType(favoriteClient)
              );
            });
          } else {
            resultFavorites = favoriteClients.filter((item) => {
              let favoriteClient = this.oeLookup.clientNow(item.id);

              return (
                favoriteClient != null &&
                favoriteClient instanceof CubiqTS.ClientImpl &&
                this.clientVisibleAndAvailableForEntryType(favoriteClient) &&
                ClientLookupService.isMatch(searchToken, favoriteClient)
              );
            });
          }
          // remove inappropriate clients
          resultFavorites = resultFavorites.filter((item) =>
            this.clientVisibleAndAvailableForEntryType(item),
          );
          // update label based on selected tab
          if (Array.isArray(resultFavorites) && resultFavorites.length > 0) {
            resultFavorites = resultFavorites.map((item) =>
              item instanceof CubiqTS.ClientImpl
                ? this.clientTransformResult(item)
                : item instanceof ICAutocompleteClientModel
                  ? this.clientTransformResult(this.oeLookup.clientNow(item.id))
                  : item,
            );
          }
        }

        // SEARCH REGULAR
        let clients: Array<CubiqTS.ClientImpl> = this.oeLookup.clientsForOrderEntryNow(
          this.entryType,
          searchToken,
          this.config.resultsToShow,
        );
        // remove inappropriate clients
        clients = clients.filter((item) => this.clientVisibleAndAvailableForEntryType(item));
        resultRegular = this.clientTransformResults(clients);
      }

      /* *********** */
      // HANDLE FAVORITES
      if (Array.isArray(resultFavorites) && resultFavorites.length > 0) {
        // remove if too many favorites
        while (resultFavorites.length > this.config.favoritesSize) {
          resultFavorites.pop();
        }
        // sort favorites
        if (Array.isArray(resultFavorites) && resultFavorites.length > 1) {
          resultFavorites.sort((a, b) => a["id"].localeCompare(b["id"]));
        }
        // add line on the last favorite
        resultFavorites[resultFavorites.length - 1].lastFavorite = true;
      }

      // HANDLE REGULARS
      // avoid duplicates in regular items and trim to results to show config
      let tempResultRegular: Array<ICAutocompleteClientModel> = Array<ICAutocompleteClientModel>();
      for (
        let i = 0;
        i < resultRegular.length && i < this.config.resultsToShow - resultFavorites.length;
        i++
      ) {
        if (
          !this.config.removeFavoriteDuplicates ||
          resultFavorites.findIndex((item) => item.id === resultRegular[i].id) < 0
        ) {
          tempResultRegular.push(resultRegular[i]);
        }
      }
      resultRegular = tempResultRegular;

      // sort regulars
      if (Array.isArray(resultRegular) && resultRegular.length > 1) {
        resultRegular.sort((a, b) => a["id"].localeCompare(b["id"]));
      }

      // ASSIGNMENT
      if (
        Array.isArray(resultFavorites) &&
        resultFavorites.length > 0 &&
        Array.isArray(resultRegular) &&
        resultRegular.length > 0
      ) {
        this.data.filteredItems = resultFavorites.concat(resultRegular);
      } else if (Array.isArray(resultFavorites) && resultFavorites.length > 0) {
        this.data.filteredItems = resultFavorites;
      } else if (Array.isArray(resultRegular) && resultRegular.length > 0) {
        this.data.filteredItems = resultRegular;
      } else {
        this.data.filteredItems = [];
      }
    } catch (ex) {
      logger.out.error("ERROR: BfxAutocompleteInputComponent.lookupClient(): Exception:", ex);
    }
  }

  protected clientValidate(): boolean {
    try {
      let selectedItem = this.data.item;
      if (selectedItem != null && selectedItem !== "") {
        if (
          selectedItem instanceof Object &&
          selectedItem.hasPrivatization &&
          (this.entryType !== OENEntryType.ALGO || selectedItem.isAutoFillEnabled)
        ) {
          // ALGO autoFillEnabled check
          // part of a locked & privatized control
          this.data.state = ICNInputControlState.VALID;
          return true;
        } else if (selectedItem instanceof Object) {
          let client = this.oeLookup.clientNow(selectedItem.value);
          if (this.clientVisibleAndAvailableForEntryType(client)) {
            this.data.state = ICNInputControlState.VALID;
            this.setDataValue(client, true, true);
            return true;
          } else {
            this.data.state = ICNInputControlState.INVALID;
            this.setDataValue(null, false, true, false);
            return false;
          }
        } else if (typeof selectedItem === "string") {
          let clientSet = this.oeLookup.clientsForOrderEntryNow(this.entryType, selectedItem, 1);
          let index = null;
          if (Array.isArray(clientSet) && clientSet.length >= 1) {
            for (let i = 0; i < clientSet.length; i++) {
              if (
                this.clientVisibleAndAvailableForEntryType(clientSet[i]) &&
                selectedItem.toLowerCase() ===
                  clientSet[i][this.data.selectedAutocompleteDisplayField].toLowerCase()
              ) {
                index = i;
              }
            }
          }

          if (typeof index === "number") {
            this.data.state = ICNInputControlState.VALID;
            this.setDataValue(clientSet[index], true, true);
            return true;
          } else {
            this.data.state = ICNInputControlState.INVALID;
            this.setDataValue(null, false, true, false);
            return false;
          }
        }
      } else {
        if (this.data.state !== ICNInputControlState.INACTIVE && !this.input_allowEmpty) {
          this.data.state = ICNInputControlState.INVALID;
        } else if (this.data.state !== ICNInputControlState.INACTIVE && this.input_allowEmpty) {
          this.data.state = ICNInputControlState.INACTIVE;
          this.setDataValue(null, false, true, false, true);
          return true;
        }
        this.setDataValue(null, false, true, false);
        return false;
      }
    } catch (ex) {
      logger.out.error("ERROR: BfxAutocompleteInputComponent.validateClient(): Exception:", ex);
      this.data.state = ICNInputControlState.INVALID;
      this.setDataValue(null, false, true, false);
      return false;
    }
  }

  private clientVisibleAndAvailableForEntryType(
    clientInput: CubiqTS.ClientImpl | ICAutocompleteClientModel,
  ): boolean {
    let client = clientInput;
    if (client instanceof ICAutocompleteClientModel) {
      client = this.oeLookup.clientNow(client.id);
    }

    if (client instanceof CubiqTS.ClientImpl) {
      let branch = this.oeLookup.branch(client.branchCd);
      if (typeof client.clientId != "string" || !client.ordersEnabledFg) {
        return false;
      } else if (
        (this.entryType === OENEntryType.SPOT || this.entryType === OENEntryType.NDF) &&
        (branch == null || !branch.allowLimitOrdersFg)
      ) {
        return false;
      } else if (this.entryType == OENEntryType.ALGO && !client.autoFillEnabled) {
        // algos are not branch limited as per KD
        return false;
      } else if (
        this.entryType == OENEntryType.BENCHMARK &&
        (branch == null || !branch.allowBenchmarkOrdersFg)
      ) {
        return false;
      } else if (
        this.visibilityService._getVisibilityType(client) != ClientAccountVisibilityType.Full
      ) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  private clientTransformResults(
    data: Array<CubiqTS.ClientImpl>,
  ): Array<ICAutocompleteClientModel> {
    let result = Array<ICAutocompleteClientModel>();
    for (let itemClientImpl of data) {
      try {
        let item = this.clientTransformResult(itemClientImpl);
        result.push(item);
      } catch (ex) {
        logger.out.error("EXCEPTION: BfxAutocompleteInputComponent.clientTransformResults(): ", ex);
      }
    }
    return result;
  }

  private clientTransformResult(client: CubiqTS.ClientImpl): ICAutocompleteClientModel {
    if (client instanceof CubiqTS.ClientImpl) {
      let model = new ICAutocompleteClientModel(); // value => id
      model.value = client.clientId;
      model.id = client.clientId;
      model.name = client.clientName;
      model.label = `${client[this.data.selectedAutocompleteDisplayField]}`;
      model.bookingCode = client.bookingCode;
      model.smartLabel = `${client.clientId} ｜ ${client.clientName} ｜ ${client.bookingCode}`;
      model.hasPrivatization = StaticDataPrivatizationType.hasPrivatization(client);
      model.isAutoFillEnabled = client.autoFillEnabled;
      model.branchCd = client.branchCd;
      return model;
    } else {
      throw "Can not transform, Item is null or wrong type (ClientImpl)!";
    }
  }

  /********************/
  /* CLIENT TRADER */

  private clientTraderLookup(event): void {
    let result: Array<ICAutocompleteGenericModel> = [];
    let searchToken: string = event.query.toLowerCase();

    if (this.data.dataset != null && this.data.dataset.length > 0) {
      // client is selected, local filtering
      for (let item of this.data.dataset) {
        if (item.label.toLowerCase().indexOf(searchToken) >= 0) {
          result.push(item);
        }
        if (this.config.resultsToShow > 0 && result.length >= this.config.resultsToShow) {
          this.data.filteredItems = result;
          return;
        }
      }
    }

    this.data.filteredItems = result;
  }

  /**
   * This method should be invoked when the clientTrader constraint (the selected client) changes,
   * It creates/loads a dataset of UserImpls, associated with that client, which are transformed into BfxAutocompleteAccountModel.
   * In case the dataset consists of a single account it is immediately selected.
   */

  protected clientTraderLoadDataset(): void {
    let clientId = null;

    if (this.input_constraint instanceof CubiqTS.ClientImpl) {
      clientId = (<CubiqTS.ClientImpl>this.input_constraint).clientId;
    } else if (this.input_constraint instanceof CubiqTS.AccountImpl) {
      clientId = (<CubiqTS.AccountImpl>this.input_constraint).clientId;
    }

    if (clientId !== null && StaticDataPrivatizationType.hasPrivatization(this.input_constraint)) {
      let clientId = (<CubiqTS.ClientImpl>this.input_constraint).extendedInfo.get(
        "__bfx_privatization_key",
      );

      this._userLookup.getUsersByClientCode(clientId).then((resultSet) => {
        if (resultSet != null && resultSet.length > 0) {
          this.data.dataset = this.clientTraderTransformResults(resultSet);
          this.data.dataset_input_constraint = clientId;
          if (this.data.dataset.length === 1) {
            this.data.disabled = false;
          }
        } else {
          this.data.dataset = [];
          this.data.dataset_input_constraint = clientId;
        }
      });
      return;
    }

    if (clientId !== null) {
      this._userLookup.getUsersByClientCode(clientId).then((resultSet) => {
        if (resultSet != null && resultSet.length > 0) {
          this.data.dataset = this.clientTraderTransformResults(resultSet);
          this.data.dataset_input_constraint = clientId;
          if (this.data.dataset.length > 0) {
            if (this.data.value != null && this.data.value instanceof ICAutocompleteGenericModel) {
              let index = this.data.dataset
                .map((item: ICAutocompleteGenericModel) => {
                  // Should be UserImpl from which we get the userCode
                  return item.value?.userCode;
                })
                .indexOf(this.data.value.value.userCode);
              if (index >= 0) {
                this.setDataValue(this.data.dataset[index], true, true, true, true, false);
                this.data.disabled = false;
                return;
              }
            } else if (this.data.value != null && this.data.value instanceof CubiqTS.UserImpl) {
              let index = this.data.dataset
                .map((item: ICAutocompleteGenericModel) => {
                  // Should be UserImpl from which we get the userCode
                  return item.value?.userCode;
                })
                .indexOf(this.data.value.userCode);
              if (index >= 0) {
                this.setDataValue(this.data.dataset[index], true, true, true, true, false);
                this.data.disabled = false;
                return;
              }
            }
            //                        this.data.item = null;
            this.data.disabled = false;
          } else {
            //                        this.data.item = null;
            this.data.disabled = false;
          }
        } else {
          this.data.dataset = [];
          this.data.dataset_input_constraint = clientId;
          this.data.state = ICNInputControlState.INACTIVE;
          this.setDataValue(null, false, true, true, true, false);
          this.data.disabled = true;
        }
      });
    } else {
      this.data.dataset = [];
      this.data.dataset_input_constraint = null;
      this.data.state = ICNInputControlState.INACTIVE;
      this.setDataValue(null, false, true, true, true, false);
      this.data.disabled = true;
    }
  }

  private clientTraderTransformResults(
    data: Array<CubiqTS.UserImpl>,
  ): Array<ICAutocompleteGenericModel> {
    let result = Array<ICAutocompleteGenericModel>();
    for (let itemUserImpl of data) {
      try {
        let item = this.clientTraderTransformResult(itemUserImpl);
        result.push(item);
      } catch (ex) {
        logger.out.error(
          "EXCEPTION: BfxAutocompleteInputComponent.clientTraderTransformResults(): ",
          ex,
        );
      }
    }
    return result;
  }

  private clientTraderTransformResult(itemUserImpl: CubiqTS.UserImpl): ICAutocompleteGenericModel {
    if (itemUserImpl != null && itemUserImpl instanceof CubiqTS.UserImpl) {
      let item = new ICAutocompleteGenericModel();
      item.value = itemUserImpl;
      item.label = itemUserImpl.userName;
      item.hasPrivatization = StaticDataPrivatizationType.hasPrivatization(itemUserImpl);
      return item;
    } else {
      logger.out.error(
        "EXCEPTION: BfxAutocompleteInputComponent.clientTraderTransformResult(): item= ",
        itemUserImpl,
      );
      throw "Can not transform, Item is null or wrong type (userImpl)!";
    }
  }

  protected clientTraderValidate(): boolean {
    let selectedItem = this.data.item;
    if (
      (selectedItem == null || selectedItem === "") &&
      this.data.state !== ICNInputControlState.INACTIVE
    ) {
      this.data.state = ICNInputControlState.INACTIVE;
      this.setDataValue(null, false, true);
      return true;
    } else if (Array.isArray(this.data.dataset)) {
      let result = this.data.dataset.find((item) => {
        if (selectedItem != null && selectedItem.label != null) {
          return item.label.toLowerCase() === selectedItem.label.toLowerCase();
        } else if (typeof selectedItem == "string") {
          return item.label.toLowerCase() === selectedItem.toLowerCase();
        }
        return false;
      });
      if (result != null) {
        this.setDataValue(result, true, true);
        return true;
      }
    } else {
      this.data.state = ICNInputControlState.INACTIVE;
    }
    if (this.data.state !== ICNInputControlState.INACTIVE) {
      // added for reset
      this.data.state = ICNInputControlState.INVALID;
      this.setDataValue(selectedItem, false, true, false);
    }
    return false;
  }

  public registerUserModelUpdates(): void {
    this.visibilityService.account$.subscribe((value) => {
      switch (this.data.mode) {
        case ICNAutocompleteMode.ACCOUNT:
          if (
            value.isTradable !== value.wasTradable &&
            this.data.item instanceof ICAutocompleteAccountModel &&
            this.data.value.id === value.id
          ) {
            this.accountValidate();
          } else if (value.isTradable !== value.wasTradable) {
            this.accountLoadDataset();
          }
          break;
        default:
          break;
      }
    });

    this.visibilityService.client$.subscribe((value) => {
      switch (this.data.mode) {
        case ICNAutocompleteMode.CLIENT:
          if (
            value.isTradable !== value.wasTradable &&
            this.data.item instanceof ICAutocompleteClientModel &&
            this.data.item.id === value.clientId
          ) {
            this.clientValidate();
          } else if (value.isTradable !== value.wasTradable) {
            this.clientLoadDataset();
          }
          break;
        default:
          break;
      }
    });

    this._userLookup.getModelUpdateSubject().subscribe((update: CubiqTS.ModelUpdateImpl) => {
      if (update instanceof CubiqTS.ModelUpdateImpl) {
        switch (this.data.mode) {
          case ICNAutocompleteMode.CLIENT_TRADER:
            this.updateClientTraderModel(update);
            break;
          default:
            // FIXME if this.data.mode was set earlier we could safely unsubscribe / never subscribe
            break;
        }
      }
    });
  }

  private updateClientTraderModel(modelUpdateEvent) {
    if (modelUpdateEvent.payLoad instanceof CubiqTS.UserImpl) {
      let clientId = null;

      if (this.input_constraint instanceof CubiqTS.ClientImpl) {
        clientId = (<CubiqTS.ClientImpl>this.input_constraint).clientId;
      } else if (this.input_constraint instanceof CubiqTS.AccountImpl) {
        clientId = (<CubiqTS.AccountImpl>this.input_constraint).clientId;
      }

      let ct: CubiqTS.UserImpl = modelUpdateEvent.payLoad;

      if (
        (ct && ct.clientCode === clientId) ||
        ct.clientCode === this.data.dataset_input_constraint
      ) {
        this.clientTraderLoadDataset();
      }
    }
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }
}
